import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import Cookies from 'js-cookie';
import {AppDispatch} from "../utils/store";
import {checkLoginStatus, clearUserData} from "../utils/userSlice";
import config from "../config/config";
import AnalyticsService from "./AnalyticsService";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    _retry?: boolean;
}

const BASE_URL = config.apiUrl
// the CDN in production, the api itself everywhere else
const CONTENT_BASE_URL = config.contentUrl

export class ApiService {
    private static instance: ApiService;

    api: AxiosInstance
    // Public content reads only. Deliberately interceptor-free: `api` attaches the
    // logged-in user's bearer token to every request, and on these reads that would
    // split the CDN cache per user, force a CORS preflight the edge does not answer,
    // and hand the token to the CDN host for a document that is public anyway.
    contentApi: AxiosInstance
    private isRefreshing: boolean = false;
    private failedQueue: Array<{
        resolve: (value?: any) => void,
        reject: (value?: any) => void
    }> = [];

    private constructor(private dispatch: AppDispatch) {
        this.api = axios.create({
            baseURL: BASE_URL,
        });

        this.contentApi = axios.create({
            baseURL: CONTENT_BASE_URL,
        });

        this.initInterceptors()
    }

    public static getInstance(dispatch: AppDispatch): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService(dispatch);
        }

        return ApiService.instance;
    }

    processQueue = (error: any, token: any = null) => {
        this.failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });

        this.failedQueue = [];
    };

    initInterceptors() {

        this.api.interceptors.request.use(config => {
            const token = Cookies.get('accessToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, error => {
            return Promise.reject(error);
        });
        this.api.interceptors.response.use((response: AxiosResponse) => {
            return response;
        }, (error: any) => {
            const originalRequest: CustomAxiosRequestConfig = error.config;

            if (error.response?.status === 401 && !originalRequest._retry) {
                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.failedQueue.push({resolve, reject})
                    }).then(token => {
                        originalRequest.headers!!['Authorization'] = 'Bearer ' + token;
                        return this.api(originalRequest);
                    });
                }

                originalRequest._retry = true;
                this.isRefreshing = true;

                const refreshToken: string | undefined = Cookies.get('refreshToken');
                if (refreshToken) {
                    return new Promise((resolve, reject) => {
                        axios.post(`${BASE_URL}/oauth/refresh`, {}, {
                            headers: {
                                'Authorization': `Bearer ${refreshToken}`
                            }
                        })
                            .then(({data}) => {
                                Cookies.set('accessToken', data.accessToken, {
                                    path: '/',
                                    expires: 7
                                });
                                if (data.refreshToken) {
                                    Cookies.set('refreshToken', data.refreshToken, {
                                        path: '/',
                                        expires: 7
                                    });
                                }
                                Cookies.set('userId', data.userId, {
                                    path: '/',
                                    expires: 7
                                });

                                const analyticsService = AnalyticsService.getInstance();
                                analyticsService.setUserId(data.userId)

                                this.dispatch(checkLoginStatus());
                                originalRequest.headers!!['Authorization'] = 'Bearer ' + data.accessToken;
                                this.processQueue(null, data.accessToken);
                                resolve(this.api(originalRequest));
                            })
                            .catch((err) => {
                                console.error("refreshToken failed", err);
                                clearUserData(this.dispatch)
                                this.processQueue(err);
                                reject(err);
                            })
                            .finally(() => {
                                this.isRefreshing = false;
                            });
                    });
                } else {
                    console.error("no refreshToken found");
                    this.isRefreshing = false;
                    clearUserData(this.dispatch)
                    this.processQueue(error);
                    return Promise.reject(error);
                }
            }

            return Promise.reject(error);
        });
    }
}
